import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { CarouselType } from 'rev-portal/branding/BrandingSettings.Contract';
import { WEBCAST_FULLSCREEN_STATE, WEBCAST_VIEW_STATE } from 'rev-portal/scheduledEvents/webcast/Constants';
import { PORTAL_MEDIA_STATE, PORTAL_VIDEO_PLAYBACK_STATE, MY_PLAYLIST_STATE, PLAYLIST_DETAIL_STATE, FEATURED_PLAYLIST_DETAIL_STATE, PORTAL_TEAM_STATE, PORTAL_TEAM_EDIT_STATE, PORTAL_MEDIA_CATEGORY_DETAILS_STATE, PORTAL_MEDIA_EDIT_STATE, PORTAL_MEDIA_EXPIRATIONS_STATE, PORTAL_MEDIA_MY_SUBSCRIPTIONS_STATE, PORTAL_MEDIA_SEARCH_STATE, PORTAL_MEDIA_UPLOADS_STATE, PORTAL_MEDIA_PENDING_VIDEOS_STATE, PORTAL_MEDIA_BROWSE_STATE, PORTAL_DASHBOARD_STATE, PORTAL_MANAGE_SUBSCRIPTIONS_STATE, PUBLIC_FEATURED_PLAYLIST_DETAIL_STATE } from 'rev-shared/bootstrap/AppStateNames';

import { PushBus } from 'rev-shared/push/PushBus.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';

import { BrandingService } from './Branding.Service';
import { BrandingSettings } from './BrandingSettings.Contract';
import { setOpacity } from './ColorUtility';

export const THEMED_STATES = [
	'portal.edit-category',
	'portal.userProfile',
	FEATURED_PLAYLIST_DETAIL_STATE,
	PUBLIC_FEATURED_PLAYLIST_DETAIL_STATE,
	MY_PLAYLIST_STATE,
	PLAYLIST_DETAIL_STATE,
	PORTAL_DASHBOARD_STATE,
	PORTAL_MEDIA_BROWSE_STATE,
	PORTAL_MEDIA_CATEGORY_DETAILS_STATE,
	PORTAL_MEDIA_EDIT_STATE,
	PORTAL_MEDIA_EXPIRATIONS_STATE,
	PORTAL_MEDIA_MY_SUBSCRIPTIONS_STATE,
	PORTAL_MEDIA_PENDING_VIDEOS_STATE,
	PORTAL_MEDIA_SEARCH_STATE,
	PORTAL_MEDIA_STATE,
	PORTAL_MEDIA_UPLOADS_STATE,
	PORTAL_MANAGE_SUBSCRIPTIONS_STATE,
	PORTAL_TEAM_EDIT_STATE,
	PORTAL_TEAM_STATE,
	PORTAL_VIDEO_PLAYBACK_STATE,
	WEBCAST_FULLSCREEN_STATE,
	WEBCAST_VIEW_STATE
];

@Injectable({
	providedIn: 'root'
})
export class ThemeService {

	public isPreviewActive: boolean;
	public isCustomThemeActive: boolean;
	public initialized: boolean;
	public initializationPromise: Promise<void>;

	public get brandingSettings(): BrandingSettings {
		return this.brandingSettingsSubject$.value;
	}

	private baseSettings: BrandingSettings = new BrandingSettings();
	private readonly overrideSettingsStack: Array<Partial<BrandingSettings>> = [];
	public readonly brandingSettingsSubject$ = new BehaviorSubject<BrandingSettings>(this.baseSettings);
	public readonly brandingSettings$: Observable<BrandingSettings> = this.brandingSettingsSubject$.asObservable();

	public get accentFontColor(): string {
		return this.brandingSettings.themeSettings.accentFontColor;
	}

	public get accentColor(): string {
		return this.brandingSettings.themeSettings.accentColor;
	}

	public get headerFontColor(): string {
		return this.brandingSettings.headerSettings.fontColor;
	}

	public get headerBackgroundColor(): string {
		return this.brandingSettings.headerSettings.backgroundColor;
	}

	public get primaryColor(): string {
		return this.brandingSettings.themeSettings.primaryColor;
	}

	public get primaryFontColor(): string {
		return this.brandingSettings.themeSettings.primaryFontColor;
	}

	public get primaryFontFade30(): string {
		return setOpacity(this.primaryFontColor, .3);
	}

	constructor(
		private readonly BrandingService: BrandingService,
		private readonly UserContext: UserContextService,
		private readonly PushBus: PushBus
	){
		UserContext.userIdChanged$.subscribe(() => {
			if (this.brandingSettings.homepageSettings.carousels?.some(c => c.type === CarouselType.TeamVideos && !c.linkName)) {
				this.reset();
			}
			if (!UserContext.isUserLoggedIn()) {
				this.endCustomThemeMode();
			}
		});
	}

	public initialize(): Promise<void> {
		if (this.initializationPromise) {
			return this.initializationPromise;
		}

		this.initializationPromise = this.loadSettings();

		return this.initializationPromise.then(() => {
			this.initialized = true;
			return this.subscribePush();
		});
	}

	public loadSettings(): Promise<void> {
		return this.BrandingService.getAppBranding()
			.then(brandingSettings => {
				this.baseSettings = brandingSettings;
				this.updateSettings();
			});
	}

	public setPreviewMode(brandingSettings: BrandingSettings): void {
		this.isPreviewActive = true;
		this.overrideSettingsStack.push(brandingSettings);
		this.updateSettings();
	}

	public endPreviewMode(): BrandingSettings {
		const previewTheme = this.overrideSettingsStack.pop();
		this.isPreviewActive = this.overrideSettingsStack.length > 0;
		this.updateSettings();
		return previewTheme as BrandingSettings;
	}

	public setCustomThemeMode(brandingSettings: Partial<BrandingSettings>): void {
		if(this.isPreviewActive) {
			return;
		}

		this.overrideSettingsStack.push(brandingSettings);
		this.isCustomThemeActive = true;
		this.updateSettings();
	}

	public endCustomThemeMode(): BrandingSettings {
		if(this.isPreviewActive) {
			return;
		}

		const previewTheme = this.overrideSettingsStack.pop();
		this.isCustomThemeActive = this.overrideSettingsStack.length > 0;
		this.updateSettings();
		return previewTheme as BrandingSettings;
	}

	public getBaseSettings(): BrandingSettings {
		return this.baseSettings;
	}

	private getOverrideSettings(): BrandingSettings {
		const baseSettings = this.baseSettings;
		const overrideSettings = this.overrideSettingsStack[this.overrideSettingsStack.length - 1];
		if (this.isCustomThemeActive) {
			return {
				...baseSettings,
				themeSettings: overrideSettings.themeSettings ? {
					accentColor: overrideSettings.themeSettings.accentColor || baseSettings.themeSettings.accentColor,
					accentFontColor: overrideSettings.themeSettings.accentFontColor || baseSettings.themeSettings.accentFontColor,
					primaryColor: overrideSettings.themeSettings.primaryColor || baseSettings.themeSettings.primaryColor,
					primaryFontColor: overrideSettings.themeSettings.primaryFontColor || baseSettings.themeSettings.primaryFontColor,
					systemTitle: overrideSettings.themeSettings.systemTitle || baseSettings.themeSettings.systemTitle,
					fontId: overrideSettings.themeSettings.fontId || baseSettings.themeSettings.fontId,
					fontUri: overrideSettings.themeSettings.fontUri || baseSettings.themeSettings.fontUri,
					logoFile: overrideSettings.themeSettings.logoFile || baseSettings.themeSettings.logoFile,
					logoImageId: overrideSettings.themeSettings.logoImageId || baseSettings.themeSettings.logoImageId,
					logoUri: overrideSettings.themeSettings.logoUri || baseSettings.themeSettings.logoUri,
					faviconFile: overrideSettings.themeSettings.faviconFile || baseSettings.themeSettings.faviconFile,
					faviconId: overrideSettings.themeSettings.faviconId || baseSettings.themeSettings.faviconId,
					faviconUri: overrideSettings.themeSettings.faviconUri || baseSettings.themeSettings.faviconUri
				} : baseSettings.themeSettings,
				headerSettings: overrideSettings.headerSettings ? {
					...baseSettings.headerSettings,
					backgroundColor: overrideSettings.headerSettings.backgroundColor || baseSettings.headerSettings.backgroundColor,
					fontColor: overrideSettings.headerSettings.fontColor || baseSettings.headerSettings.fontColor
				} : baseSettings.headerSettings
			};
		}
		if(this.isPreviewActive) {
			return overrideSettings as BrandingSettings;
		}

		return this.baseSettings;
	}

	private subscribePush(): void {
		this.PushBus.subscribe(this.UserContext.getAccount().id, {
			BrandingSettingsSaved: e => {
				if (e.isTeamVideosCarouselAvailable) {
					this.loadSettings();
				}
				else {
					this.baseSettings = this.BrandingService.readBrandingSettings(e.settings);
					this.updateSettings();
				}
			},
			CarouselsRemoved: e => {
				e.carousels.forEach((removedCarousel: CarouselType) => {
					this.brandingSettings.homepageSettings.carousels.splice(this.brandingSettings.homepageSettings.carousels.findIndex(c => c.type === removedCarousel), 1);
				});
				this.brandingSettingsSubject$.next(this.brandingSettings);
			}
		});
	}

	private updateSettings(): void {
		this.brandingSettingsSubject$.next(this.getOverrideSettings());
	}

	private reset(): void {
		this.initializationPromise = null;
		this.initialized = false;
	}
}
